import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  companyProductSystemUseCase,
  orderMailboxUseCase,
} from "../../../di/container";
import type {
  ProductLightDto,
  OptionDto,
} from "../../../domain/types/company/company-product-system";
import type {
  CollectedEmailDetail,
  CreateOrderFromEmailRequest,
  AdminOrderOption,
  EstimateItemInput,
} from "../../../domain/types/order-mailbox";
import { Button } from "presentation/components/UI/Button";
import { LoadingSpinner } from "presentation/components/UI/LoadingSpinner";

interface MailOrderComposerProps {
  companyId: number;
  detail: CollectedEmailDetail;
  isCreatingOrder: boolean;
  onCreateOrder: (request: CreateOrderFromEmailRequest) => void;
  onNotify: (message: string) => void;
}

/**
 * 메일 상세 우측 패널: 상품/옵션 선택 후 주문 구성.
 * AI 매칭 결과(matchedProductId/matchedOptionIds)를 최초 1회 프리셀렉트한다.
 */
const MailOrderComposer: React.FC<MailOrderComposerProps> = ({
  companyId,
  detail,
  isCreatingOrder,
  onCreateOrder,
  onNotify,
}) => {
  const [products, setProducts] = useState<ProductLightDto[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    detail.order.matchedProductId,
  );
  const [options, setOptions] = useState<OptionDto[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  // 선택된 옵션 값 id 집합 (OptionAttributeValue.id)
  const [selectedValueIds, setSelectedValueIds] = useState<Set<number>>(
    new Set(),
  );
  const [quantity, setQuantity] = useState<number>(detail.order.quantity ?? 1);
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [isDownloadingEstimate, setIsDownloadingEstimate] = useState(false);
  const [isSendingEstimate, setIsSendingEstimate] = useState(false);

  // AI 매칭 옵션 프리셀렉트는 최초 옵션 로드 시 한 번만 적용
  const optionPreselectPendingRef = useRef(
    detail.order.matchedProductId !== null &&
      detail.order.matchedOptionIds.length > 0,
  );

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId) ?? null,
    [products, selectedProductId],
  );

  // 상품 목록 로드 (카테고리 평탄화 + 활성 상품만)
  useEffect(() => {
    let cancelled = false;
    setIsLoadingProducts(true);
    companyProductSystemUseCase
      .getCategories(companyId)
      .then((categories) => {
        if (cancelled) return;
        const flat = categories
          .filter((c) => c.isEnable)
          .flatMap((c) => c.products)
          .filter((p) => p.isEnable !== false);
        setProducts(flat);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingProducts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // 상품 선택 시 단가 초기화 (상품 목록 로드 후 프리셀렉트 반영 포함)
  useEffect(() => {
    if (selectedProduct) setUnitPrice(selectedProduct.price);
  }, [selectedProduct]);

  // 상품 선택 시 옵션 로드 + 프리셀렉트
  useEffect(() => {
    if (!selectedProductId) {
      setOptions([]);
      setSelectedValueIds(new Set());
      return;
    }
    let cancelled = false;
    setIsLoadingOptions(true);
    companyProductSystemUseCase
      .getOptions(companyId, selectedProductId)
      .then((result) => {
        if (cancelled) return;
        const active = result.filter((o) => !o.isDeleted);
        setOptions(active);

        // AI 매칭 옵션 프리셀렉트 (매칭 상품과 동일할 때만, 1회 소비)
        if (
          optionPreselectPendingRef.current &&
          selectedProductId === detail.order.matchedProductId
        ) {
          const loadedValueIds = new Set(
            active.flatMap((o) =>
              o.attributes
                .filter((a) => !a.isDeleted)
                .flatMap((a) =>
                  a.values.filter((v) => v.isEnabled).map((v) => v.id),
                ),
            ),
          );
          setSelectedValueIds(
            new Set(
              detail.order.matchedOptionIds.filter((id) =>
                loadedValueIds.has(id),
              ),
            ),
          );
        } else {
          setSelectedValueIds(new Set());
        }
        optionPreselectPendingRef.current = false;
      })
      .catch((error) => {
        console.error("Error fetching options:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    companyId,
    selectedProductId,
    detail.order.matchedProductId,
    detail.order.matchedOptionIds,
  ]);

  // 선택된 옵션 값 목록 (요청 생성/가격 계산용)
  const selectedOptionEntries = useMemo(() => {
    const entries: {
      valueId: number;
      optionName: string;
      price: number;
    }[] = [];
    for (const option of options) {
      for (const attr of option.attributes) {
        if (attr.isDeleted) continue;
        for (const value of attr.values) {
          if (!value.isEnabled || !selectedValueIds.has(value.id)) continue;
          const groupName =
            option.name === attr.name
              ? option.name
              : `${option.name} > ${attr.name}`;
          entries.push({
            valueId: value.id,
            optionName: `${groupName}: ${value.value ?? "-"}`,
            price: value.price ?? 0,
          });
        }
      }
    }
    return entries;
  }, [options, selectedValueIds]);

  const optionUnitTotal = selectedOptionEntries.reduce(
    (sum, e) => sum + e.price,
    0,
  );
  const totalPrice = (unitPrice + optionUnitTotal) * quantity;

  const toggleValue = (
    attrValueIds: number[],
    valueId: number,
    isMultiSelectable: boolean,
  ) => {
    setSelectedValueIds((prev) => {
      const next = new Set(prev);
      if (next.has(valueId)) {
        next.delete(valueId);
        return next;
      }
      if (!isMultiSelectable) {
        // 단일 선택 속성: 같은 속성의 다른 값 해제
        attrValueIds.forEach((id) => next.delete(id));
      }
      next.add(valueId);
      return next;
    });
  };

  const buildOrdererFields = (): Pick<
    CreateOrderFromEmailRequest,
    "purchaserId" | "guestInfo"
  > => ({
    purchaserId: detail.matchedUser?.id ?? null,
    guestInfo: detail.matchedUser
      ? null
      : {
          name: detail.sender.name ?? detail.sender.emailAddress,
          email: detail.sender.emailAddress,
          mobile: detail.sender.phone,
        },
  });

  const handleCreateOrder = () => {
    if (quantity < 1) {
      onNotify("수량은 1 이상이어야 합니다.");
      return;
    }

    const fallbackName =
      detail.order.productName ?? detail.mail.mailSubject ?? "메일 주문";

    if (selectedProduct) {
      // 상품 선택 경로: productId + options (BE 규약상 productPrice 는 양수 필수)
      if (unitPrice < 1) {
        onNotify("상품 단가는 1원 이상이어야 합니다.");
        return;
      }
      const adminOptions: AdminOrderOption[] = selectedOptionEntries.map(
        (entry) => ({
          combinationId: null,
          optionName: entry.optionName,
          optionPrice: entry.price,
          optionCount: quantity,
        }),
      );
      onCreateOrder({
        name: selectedProduct.name,
        productId: selectedProduct.id,
        productCount: quantity,
        productPrice: unitPrice,
        options: adminOptions.length > 0 ? adminOptions : null,
        initialStatus: "BEFORE_PAYMENT",
        ...buildOrdererFields(),
      });
      return;
    }

    // 상품 미선택: 커스텀 주문 경로 (기존 동작 유지)
    onCreateOrder({
      name: fallbackName,
      customOrderInfo: {
        customProductName: fallbackName,
        productPrice: unitPrice >= 0 ? unitPrice : 0,
        productCount: quantity,
      },
      initialStatus: "BEFORE_PAYMENT",
      ...buildOrdererFields(),
    });
  };

  /** 견적서 품목 구성 (현재 선택 상태 → 단일 품목, 옵션은 규격 컬럼에 줄바꿈 나열) */
  const buildEstimateItems = (): EstimateItemInput[] | null => {
    if (quantity < 1) {
      onNotify("수량은 1 이상이어야 합니다.");
      return null;
    }
    const productName =
      selectedProduct?.name ??
      detail.order.productName ??
      detail.mail.mailSubject ??
      "메일 주문";
    const optionDetail =
      selectedOptionEntries.length > 0
        ? selectedOptionEntries
            .map((entry) =>
              entry.price !== 0
                ? `${entry.optionName} (+${entry.price.toLocaleString()}원)`
                : entry.optionName,
            )
            .join("\n")
        : null;

    return [
      {
        productName,
        optionDetail,
        quantity,
        // 견적서 단가 = 상품 단가 + 옵션 단가 합 (1개 기준)
        unitPrice: unitPrice + optionUnitTotal,
        totalPrice,
      },
    ];
  };

  const handleDownloadEstimate = async () => {
    const items = buildEstimateItems();
    if (!items) return;

    setIsDownloadingEstimate(true);
    try {
      const { blob, fileName } = await orderMailboxUseCase.downloadEstimate(
        companyId,
        detail.id,
        items,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName ?? "견적서.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      onNotify("견적서 PDF 를 다운로드했습니다.");
    } catch (error) {
      onNotify(
        error instanceof Error ? error.message : "견적서 다운로드에 실패했습니다.",
      );
    } finally {
      setIsDownloadingEstimate(false);
    }
  };

  const handleSendEstimate = async () => {
    const items = buildEstimateItems();
    if (!items) return;

    setIsSendingEstimate(true);
    try {
      // toEmail 미지정 → 수집 메일 발신자에게 전송
      const recipient = await orderMailboxUseCase.sendEstimate(
        companyId,
        detail.id,
        items,
      );
      onNotify(`견적서를 ${recipient} 로 전송했습니다.`);
    } catch (error) {
      onNotify(
        error instanceof Error
          ? error.message
          : "견적서 메일 전송에 실패했습니다.",
      );
    } finally {
      setIsSendingEstimate(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 text-[14px]">
      <h3 className="font-semibold text-[16px]">주문 구성</h3>

      {/* 상품 선택 */}
      <div className="flex flex-col gap-1">
        <label className="text-[12px] font-medium text-[#555]">상품</label>
        {isLoadingProducts ? (
          <div className="py-2">
            <LoadingSpinner />
          </div>
        ) : (
          <select
            className="w-full rounded-[8px] border border-[#e0e0e0] px-3 py-2 focus:border-[#4285F4] focus:outline-none"
            value={selectedProductId ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedProductId(value === "" ? null : Number(value));
            }}
          >
            <option value="">직접 입력 (메일 내용 기반 커스텀 주문)</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name} ({product.price.toLocaleString()}원)
              </option>
            ))}
          </select>
        )}
        {!selectedProduct && (
          <span className="text-[12px] text-[#888]">
            상품을 선택하지 않으면 메일 내용으로 커스텀 주문이 생성됩니다.
          </span>
        )}
      </div>

      {/* 옵션 선택 */}
      {selectedProduct && (
        <div className="flex flex-col gap-3">
          <label className="text-[12px] font-medium text-[#555]">옵션</label>
          {isLoadingOptions ? (
            <div className="py-2">
              <LoadingSpinner />
            </div>
          ) : options.length === 0 ? (
            <span className="text-[12px] text-[#888]">
              선택 가능한 옵션이 없습니다.
            </span>
          ) : (
            options.map((option) =>
              option.attributes
                .filter((attr) => !attr.isDeleted)
                .map((attr) => {
                  const enabledValues = attr.values.filter((v) => v.isEnabled);
                  if (enabledValues.length === 0) return null;
                  const attrValueIds = enabledValues.map((v) => v.id);
                  const groupName =
                    option.name === attr.name
                      ? option.name
                      : `${option.name} > ${attr.name}`;
                  return (
                    <div key={attr.id} className="flex flex-col gap-1.5">
                      <span className="text-[13px] text-[#555]">
                        {groupName}
                        {option.isRequired && (
                          <span className="ml-1 text-[#4285F4]">*</span>
                        )}
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {enabledValues.map((value) => {
                          const isSelected = selectedValueIds.has(value.id);
                          return (
                            <button
                              key={value.id}
                              type="button"
                              onClick={() =>
                                toggleValue(
                                  attrValueIds,
                                  value.id,
                                  attr.isMultiSelectable,
                                )
                              }
                              className={`rounded-[8px] border px-3 py-1.5 text-[13px] transition-colors ${
                                isSelected
                                  ? "border-[#4285F4] bg-[#E8EFFF] text-[#4285F4]"
                                  : "border-[#e0e0e0] bg-white text-[#555] hover:bg-[#f5f5f5]"
                              }`}
                            >
                              {value.value ?? "-"}
                              {(value.price ?? 0) !== 0 && (
                                <span className="ml-1 text-[12px]">
                                  (+{(value.price ?? 0).toLocaleString()}원)
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }),
            )
          )}
        </div>
      )}

      {/* 수량 / 단가 */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[12px] font-medium text-[#555]">수량</label>
          <input
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
            className="w-full rounded-[8px] border border-[#e0e0e0] px-3 py-2 focus:border-[#4285F4] focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[12px] font-medium text-[#555]">
            단가 (원)
          </label>
          <input
            type="number"
            min={0}
            value={unitPrice}
            onChange={(e) => setUnitPrice(Number(e.target.value))}
            className="w-full rounded-[8px] border border-[#e0e0e0] px-3 py-2 focus:border-[#4285F4] focus:outline-none"
          />
        </div>
      </div>

      {/* 금액 요약 */}
      <div className="flex flex-col gap-1.5 rounded-[8px] bg-[#FAFAFA] p-4">
        <div className="flex justify-between text-[#555]">
          <span>상품 단가</span>
          <span>{unitPrice.toLocaleString()}원</span>
        </div>
        {selectedOptionEntries.map((entry) => (
          <div
            key={entry.valueId}
            className="flex justify-between text-[#888] text-[13px]"
          >
            <span>{entry.optionName}</span>
            <span>+{entry.price.toLocaleString()}원</span>
          </div>
        ))}
        <div className="flex justify-between text-[#555]">
          <span>수량</span>
          <span>× {quantity}</span>
        </div>
        <div className="mt-1 flex justify-between border-t border-[#eee] pt-2 font-semibold text-[#222]">
          <span>합계</span>
          <span>{totalPrice.toLocaleString()}원</span>
        </div>
      </div>

      {/* 주문자 정보 */}
      <div className="flex flex-col gap-1 rounded-[8px] bg-[#FAFAFA] p-4">
        <span className="text-[12px] font-medium text-[#555]">주문자</span>
        {detail.matchedUser ? (
          <span className="text-[#222]">
            {detail.matchedUser.name ?? "-"} (회원 #{detail.matchedUser.id})
          </span>
        ) : (
          <span className="text-[#222]">
            {detail.sender.name ?? detail.sender.emailAddress}{" "}
            <span className="text-[#888] text-[12px]">
              (비회원 - 게스트 주문으로 생성)
            </span>
          </span>
        )}
      </div>

      {/* 액션 버튼: 견적서 다운로드 / 견적서 메일 전송 / 주문 생성 */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          onClick={handleDownloadEstimate}
          disabled={isDownloadingEstimate || isSendingEstimate}
          className="w-full"
        >
          {isDownloadingEstimate ? "생성 중..." : "견적서 다운로드"}
        </Button>
        <Button
          variant="outline"
          onClick={handleSendEstimate}
          disabled={isDownloadingEstimate || isSendingEstimate}
          className="w-full"
        >
          {isSendingEstimate ? "전송 중..." : "견적서 메일 전송"}
        </Button>
      </div>
      <Button
        onClick={handleCreateOrder}
        disabled={isCreatingOrder}
        className="w-full"
      >
        {isCreatingOrder ? "주문 생성 중..." : "주문 생성"}
      </Button>
    </div>
  );
};

export default MailOrderComposer;
